// import _debounce from 'lodash/debounce';
import React, { useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FormSkeleton } from '../UI/Skeleton/Skeleton';
import EmptyData from '../UI/EmptyData';
import '../../screens/Admin/Performance/components/kpiModal.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';

// import { selectedKpi } from '../../redux/Slice/kpiSlice';
import { useGetKpiPerformanceByFilterQuery } from '../../redux/apis/PerformanceApi';

import { GLOBAL } from '../../utils/constant/global';
import { INPUT_LABEL } from '../../utils/constant/placeHolders';
import { performanceFormSchema } from '../../utils/schemas/performance';

const { LB_NOTES, LB_POINTS, LB_AVG } = INPUT_LABEL;

const PerformanceViewModel = ({ state, setState }) => {
  // const dispatch = useDispatch();

  const selectedPerformance = useSelector((state) => state.performance.selectedPerformance);

  const {
    data: selectedKpiData,
    isLoading: kpiPerformanceLoading,
    refetch: kpiPerformanceFetch
  } = useGetKpiPerformanceByFilterQuery({
    selectedYear: selectedPerformance?.selectedYear,
    selectedMonth: selectedPerformance?.selectedMonth,
    userId: selectedPerformance?._id
  });

  useEffect(() => {
    if (selectedPerformance?._id) kpiPerformanceFetch();
  }, [selectedPerformance]);

  useEffect(() => {
    handleSetEditFormData();
  }, [selectedKpiData]);

  const {
    register,
    reset,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(performanceFormSchema),
    values: {
      kpis:
        selectedKpiData?.data?.kpis?.length > 0
          ? selectedKpiData?.data.kpis.map((kpi) => ({
              name: kpi.value,
              value: kpi.name,
              kpiId: kpi.kpiId,
              notes: kpi.notes
            }))
          : [],
      average: 0
    }
  });

  const { fields } = useFieldArray({
    control,
    name: 'kpis'
  });

  // Watch kpis array for changes
  const kpisWatch = watch('kpis');

  useEffect(() => {
    handleAverageCalculation();
  }, [JSON.stringify(kpisWatch)]);

  const handleAverageCalculation = () => {
    // Ensure kpisWatch is an array
    if (!Array.isArray(kpisWatch) || kpisWatch.length === 0) {
      setValue('average', 0);
      return;
    }

    // Calculate the average whenever kpisWatch changes
    const total = kpisWatch.reduce((acc, kpi) => acc + (Number(kpi.name) || 0), 0);
    const average = kpisWatch.length > 0 ? total / kpisWatch.length : 0;

    // Only set value if it has changed to avoid unnecessary renders
    if (average !== getValues('average')) {
      setValue('average', Math.round(average * 100) / 100);
    }
  };

  const getErrorMessage = (field, errors) => {
    return errors[field] ? errors[field].message : '';
  };

  const handleSetEditFormData = () => {
    if (selectedKpiData?.data) {
      setValue('notes', selectedKpiData?.data?.notes || '');
    }
  };

  /* const handleDialogCancel = () => {
    setState({ ...state, isModal: { model: false, submit: false } });
    dispatch(selectedKpi({}));
  }; */

  const kpiLoaderComponent = () => {
    if (!selectedKpiData && kpiPerformanceLoading) {
      return (
        <div className="loading-card w-100">
          <FormSkeleton rows={5} twoCol />
        </div>
      );
    } else if (selectedKpiData && (!selectedKpiData.data || selectedKpiData.data.length === 0)) {
      return (
        <EmptyData
          title={GLOBAL.NO_KPI_FOUND}
          subtitle="There are no KPIs configured for this period yet."
        />
      );
    } else if (!selectedKpiData?.data?.kpis || selectedKpiData.data.kpis.length === 0) {
      return (
        <EmptyData
          title={GLOBAL.NO_KPI_FOUND}
          subtitle="There are no KPIs configured for this period yet."
        />
      );
    }
    return null; // Default return value if none of the conditions are met
  };

  return (
    <Modal
      centered
      show={state?.isModal?.model}
      size="md"
      onHide={() => {
        reset();
        setState({ ...state, isModal: { model: false, submit: false } });
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">{state?.modalHeader?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="kpi-modal-body">
        {kpiLoaderComponent()}
        {selectedKpiData?.data?.kpis?.length > 0 && (
          <form aria-disabled="true">
            <div className="deadline-form">
              {fields.map((field, index) => (
                <div key={field.id} className="mb-3">
                  <div className="d-flex">
                    <div>
                      <label htmlFor={`kpis.${index}.name`} className="form-label">
                        {selectedKpiData?.data?.kpis?.[index]?.name || 'KPI Name'}
                      </label>
                      <input
                        type="text"
                        {...register(`kpis.${index}.name`, { required: 'Name is required' })}
                        className="form-control"
                        placeholder={LB_POINTS}
                        id={`kpis.${index}.name`}
                        onChange={(data) => {
                          setValue(`kpis.${index}.name`, data.target.value);
                        }}
                        disabled={true}
                      />
                      <small className="text-danger">
                        {errors?.['kpis']?.[index]?.name?.message || ''}
                      </small>
                    </div>
                    <div className="ms-3 w-full d-flex flex-column justify-content-end">
                      <label htmlFor={`kpis.${index}.notes`} className="form-label">
                        {LB_NOTES}
                      </label>
                      <input
                        type="text"
                        {...register(`kpis.${index}.notes`)}
                        className="form-control"
                        placeholder={LB_NOTES}
                        id={`kpis.${index}.notes`}
                        onChange={(data) => {
                          setValue(`kpis.${index}.notes`, data.target.value);
                        }}
                        disabled={true}
                      />
                      <small className="text-danger">
                        {errors?.['kpis']?.[index]?.name?.message || ''}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
              <div>
                <div className="mb-3">
                  <div className="mb-3">
                    <label htmlFor="exampleFormControlInput1111" className="form-label">
                      {LB_AVG}
                    </label>
                    <input
                      type="text"
                      {...register('average')}
                      className="form-control"
                      placeholder={LB_AVG}
                      disabled={true}
                    />
                    <small className="text-danger">{getErrorMessage('average', errors)}</small>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PerformanceViewModel;
